import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, Modal, Form, Spinner, Accordion, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import QuestionBankService from '../../_services/QuestionBankService';
import type {
  ParsedQuestion,
  ImportQuestionInput,
  LookupData,
} from '../../_services/QuestionBankService';
import QuestionTagSelector, { TagSelection, AddedTag, getSelectedTagIds } from '../quick-quiz-maker/QuestionTagSelector';
import './import-docx.css';

declare global {
  interface Window {
    MathJax?: any;
  }
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const CATEGORY_LABELS: Record<number, string> = {
  26: 'SCQ',
  1: 'MCQ',
  4: 'Integer',
  [-1]: 'Comprehension',
  3: 'True/False',
  2: 'MTF',
  5: 'Int Range',
  7: 'Blanks',
  8: 'Fraction',
  0: 'Text',
};
const QUESTION_TAG_TYPE_IDS = [5, 6, 8, 9, 10, 12, 101, 102];


const CATEGORY_COLORS: Record<number, { bg: string; fg: string }> = {
  26: { bg: '#e3f2fd', fg: '#1565c0' },
  1: { bg: '#f3e5f5', fg: '#7b1fa2' },
  4: { bg: '#fff3e0', fg: '#e65100' },
  [-1]: { bg: '#e8f5e9', fg: '#2e7d32' },
  3: { bg: '#fce4ec', fg: '#c62828' },
  2: { bg: '#e0f7fa', fg: '#00695c' },
  5: { bg: '#fff8e1', fg: '#f57f17' },
  7: { bg: '#ede7f6', fg: '#4527a0' },
  8: { bg: '#e8eaf6', fg: '#283593' },
  0: { bg: '#efebe9', fg: '#4e342e' },
};

/* ------------------------------------------------------------------ */
/*  Tag Assignment State                                               */
/* ------------------------------------------------------------------ */

interface QuestionTagState {
  [questionIndex: string]: number[];
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

const ImportDocx: React.FC = () => {
  // Stage: 'upload' | 'preview' | 'done'
  const [stage, setStage] = useState<'upload' | 'preview' | 'done'>('upload');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Parsed questions
  const [questions, setQuestions] = useState<ParsedQuestion[]>([]);

  // Tag assignment per question (key = "i" or "i-j" for children)
  const [tagAssignments, setTagAssignments] = useState<QuestionTagState>({});

  // Lookups for tag dropdowns
  const [lookups, setLookups] = useState<LookupData | null>(null);

  // Bulk tag assignment via QuestionTagSelector
  const [bulkTagSelections, setBulkTagSelections] = useState<Record<number, TagSelection>>({});
  const [bulkAddedTags, setBulkAddedTags] = useState<AddedTag[]>([]);

  // Import result
  const [importedIds, setImportedIds] = useState<number[]>([]);

  // transaction_master.Id returned from /import-docx/parse — passed back to
  // /import-docx/save so each inserted question.TransactionId points to the
  // DOCX upload that created it.
  const [transactionId, setTransactionId] = useState<number | null>(null);

  // Confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // MathJax typeset — the parsed question HTML (injected via
  // dangerouslySetInnerHTML) contains <math> MathML and \[ … \] TeX delimiters
  // (the latter for determinant-style matrices, which only render correctly
  // through MathJax's TeX processor, not native browser MathML). MathJax's
  // initial page scan misses React-rendered content, so we queue an explicit
  // typeset over the page root whenever the parsed questions change.
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (stage !== 'preview' || !rootRef.current) return;
    if (!window.MathJax?.Hub?.Queue) return;
    try {
      window.MathJax.Hub.Queue(['Typeset', window.MathJax.Hub, rootRef.current]);
    } catch (e) {
      console.warn('MathJax typeset failed:', e);
    }
  }, [stage, questions]);

  // Load lookups on mount
  useEffect(() => {
    QuestionBankService.getLookups().then((res) => {
      if (res.success && res.data) setLookups(res.data);
    });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Upload & Parse                                                    */
  /* ---------------------------------------------------------------- */

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    setError('');
  };

  const handleParse = useCallback(async () => {
    if (!selectedFile) {
      setError('Please select a .docx file');
      return;
    }
    if (!selectedFile.name.toLowerCase().endsWith('.docx')) {
      setError('Only .docx files are supported');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await QuestionBankService.parseDocx(selectedFile);
      if (result.success && result.questions) {
        setQuestions(result.questions);
        setTagAssignments({});
        setTransactionId(result.transactionId ?? null);
        setStage('preview');
      } else {
        setError(result.error || 'Failed to parse DOCX file');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [selectedFile]);

  /* ---------------------------------------------------------------- */
  /*  Tag Management                                                    */
  /* ---------------------------------------------------------------- */

  const handleTagChange = (questionKey: string, tagId: number, checked: boolean) => {
    setTagAssignments((prev) => {
      const current = prev[questionKey] || [];
      if (checked) {
        return { ...prev, [questionKey]: [...current, tagId] };
      } else {
        return { ...prev, [questionKey]: current.filter((id) => id !== tagId) };
      }
    });
  };

  const handleBulkTagApply = () => {
    const selectedIds = getSelectedTagIds(bulkTagSelections, bulkAddedTags);
    if (selectedIds.length === 0) return;
    const newAssignments: QuestionTagState = { ...tagAssignments };
    questions.forEach((q, i) => {
      if (q.categoryId === -1 && q.children) {
        q.children.forEach((_child, j) => {
          const key = `${i}-${j}`;
          const existing = newAssignments[key] || [];
          const merged = Array.from(new Set([...existing, ...selectedIds]));
          newAssignments[key] = merged;
        });
      } else {
        const key = String(i);
        const existing = newAssignments[key] || [];
        const merged = Array.from(new Set([...existing, ...selectedIds]));
        newAssignments[key] = merged;
      }
    });
    setTagAssignments(newAssignments);
    setBulkTagSelections({});
    setBulkAddedTags([]);
  };

  /* ---------------------------------------------------------------- */
  /*  Import                                                            */
  /* ---------------------------------------------------------------- */

  const buildImportPayload = (): ImportQuestionInput[] => {
    return questions
      .filter((q) => q.errors.length === 0 || q.categoryId === -1)
      .map((q, i) => {
        if (q.categoryId === -1 && q.children) {
          // Comprehension
          const children: ImportQuestionInput[] = q.children
            .filter((c) => c.errors.length === 0)
            .map((child, j) => ({
              questionStatement: child.questionStatement,
              categoryId: child.categoryId,
              choices: child.choices,
              textAnswer: child.textAnswer,
              answerRangeStart: child.answerRangeStart,
              answerRangeEnd: child.answerRangeEnd,
              matchPairs: child.matchPairs,
              blanks: child.blanks,
              solution: child.solution,
              positiveMarks: child.positiveMarks,
              negativeMarks: child.negativeMarks,
              tagIds: tagAssignments[`${i}-${j}`] || [],
            }));
          return {
            questionStatement: q.questionStatement,
            categoryId: q.categoryId,
            choices: [],
            solution: q.solution,
            positiveMarks: 0,
            negativeMarks: 0,
            tagIds: [],
            children,
          };
        }
        return {
          questionStatement: q.questionStatement,
          categoryId: q.categoryId,
          choices: q.choices,
          textAnswer: q.textAnswer,
          answerRangeStart: q.answerRangeStart,
          answerRangeEnd: q.answerRangeEnd,
          matchPairs: q.matchPairs,
          blanks: q.blanks,
          solution: q.solution,
          positiveMarks: q.positiveMarks,
          negativeMarks: q.negativeMarks,
          tagIds: tagAssignments[String(i)] || [],
        };
      });
  };

  const handleImport = useCallback(async () => {
    setShowConfirmModal(false);
    setLoading(true);
    setError('');

    try {
      const payload = buildImportPayload();
      const result = await QuestionBankService.bulkImportQuestions(payload, transactionId);
      if (result.success && result.questionIds) {
        setImportedIds(result.questionIds);
        setStage('done');
      } else {
        setError(result.error || 'Failed to import questions');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  }, [questions, tagAssignments]);

  const handleReset = () => {
    setStage('upload');
    setQuestions([]);
    setTagAssignments({});
    setSelectedFile(null);
    setImportedIds([]);
    setTransactionId(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  /* ---------------------------------------------------------------- */
  /*  Count valid questions                                             */
  /* ---------------------------------------------------------------- */

  const countValidQuestions = (): number => {
    let count = 0;
    for (const q of questions) {
      if (q.categoryId === -1 && q.children) {
        count += q.children.filter((c) => c.errors.length === 0).length;
      } else if (q.errors.length === 0) {
        count++;
      }
    }
    return count;
  };

  // Collect every question/child that has validation errors, labelled with the
  // same running Q-number shown on its card, so the confirm dialog can list
  // exactly what will be skipped (mirrors the numbering in the cards list).
  const collectInvalidQuestions = (): { label: string; errors: string[] }[] => {
    const invalid: { label: string; errors: string[] }[] = [];
    let running = 0;
    for (const q of questions) {
      if (q.categoryId === -1 && q.children) {
        for (const child of q.children) {
          running += 1;
          if (child.errors.length > 0) {
            invalid.push({ label: `Q${running}`, errors: child.errors });
          }
        }
      } else {
        running += 1;
        if (q.errors.length > 0) {
          invalid.push({ label: `Q${running}`, errors: q.errors });
        }
      }
    }
    return invalid;
  };

  /* ---------------------------------------------------------------- */
  /*  Render: Upload Stage                                              */
  /* ---------------------------------------------------------------- */

  const handleDownloadSample = () => {
    const html = `<html><head><meta charset="utf-8"><style>table{border-collapse:collapse;width:100%;margin-bottom:30px}td,th{border:1px solid black;padding:8px;vertical-align:top}td:first-child{width:120px;font-weight:bold}h2{margin-top:40px}h3{color:#1565c0;margin-top:20px}</style></head><body>
<h1>Standalone Questions</h1>
<h2>1. Single Choice (SCQ)</h2>
<table><tr><td>Question</td><td colspan="2">What is the capital of France?</td></tr><tr><td>Type</td><td colspan="2">single_choice</td></tr><tr><td>Option</td><td>London</td><td>incorrect</td></tr><tr><td>Option</td><td>Paris</td><td>correct</td></tr><tr><td>Option</td><td>Berlin</td><td>incorrect</td></tr><tr><td>Option</td><td>Madrid</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">Paris is the capital of France.</td></tr><tr><td>Marks</td><td>3</td><td>1</td></tr></table>
<h2>2. Multiple Choice (MCQ)</h2>
<table><tr><td>Question</td><td colspan="2">Which are prime numbers?</td></tr><tr><td>Type</td><td colspan="2">multiple_choice</td></tr><tr><td>Option</td><td>2</td><td>correct</td></tr><tr><td>Option</td><td>4</td><td>incorrect</td></tr><tr><td>Option</td><td>7</td><td>correct</td></tr><tr><td>Option</td><td>9</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">2 and 7 are prime.</td></tr><tr><td>Marks</td><td>4</td><td>1</td></tr></table>
<h2>3. Integer</h2>
<table><tr><td>Question</td><td colspan="2">What is 15 + 27?</td></tr><tr><td>Type</td><td colspan="2">integer</td></tr><tr><td>Answer</td><td colspan="2">42</td></tr><tr><td>Solution</td><td colspan="2">15 + 27 = 42</td></tr><tr><td>Marks</td><td>3</td><td>0</td></tr></table>
<h2>4. True or False</h2>
<table><tr><td>Question</td><td colspan="2">The Earth revolves around the Sun.</td></tr><tr><td>Type</td><td colspan="2">true_false</td></tr><tr><td>Option</td><td>True</td><td>correct</td></tr><tr><td>Option</td><td>False</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">The Earth orbits the Sun.</td></tr><tr><td>Marks</td><td>2</td><td>0</td></tr></table>
<h2>5. Match the Following</h2>
<table><tr><td>Question</td><td colspan="2">Match countries with capitals.</td></tr><tr><td>Type</td><td colspan="2">match_the_following</td></tr><tr><td>Left</td><td>India</td><td>New Delhi</td></tr><tr><td>Left</td><td>France</td><td>Paris</td></tr><tr><td>Left</td><td>Japan</td><td>Tokyo</td></tr><tr><td>Solution</td><td colspan="2">Standard geography.</td></tr><tr><td>Marks</td><td>4</td><td>1</td></tr></table>
<h2>6. Integer Range</h2>
<table><tr><td>Question</td><td colspan="2">Pi lies between which two integers?</td></tr><tr><td>Type</td><td colspan="2">integer_range</td></tr><tr><td>Answer</td><td>3</td><td>4</td></tr><tr><td>Solution</td><td colspan="2">Pi = 3.14... lies between 3 and 4.</td></tr><tr><td>Marks</td><td>3</td><td>1</td></tr></table>
<h2>7. Fill in the Blanks</h2>
<table><tr><td>Question</td><td colspan="2">The capital of India is ____. The national animal is the ____.</td></tr><tr><td>Type</td><td colspan="2">fill_in_the_blanks</td></tr><tr><td>Blank</td><td colspan="2">New Delhi</td></tr><tr><td>Blank</td><td colspan="2">Tiger</td></tr><tr><td>Solution</td><td colspan="2">New Delhi and Bengal Tiger.</td></tr><tr><td>Marks</td><td>2</td><td>0</td></tr></table>
<h2>8. Fraction / Decimal</h2>
<table><tr><td>Question</td><td colspan="2">What is 22/7 rounded to 2 decimals?</td></tr><tr><td>Type</td><td colspan="2">fraction</td></tr><tr><td>Answer</td><td colspan="2">3.14</td></tr><tr><td>Solution</td><td colspan="2">22/7 = 3.14</td></tr><tr><td>Marks</td><td>3</td><td>1</td></tr></table>
<h2>9. Text / Subjective</h2>
<table><tr><td>Question</td><td colspan="2">Explain photosynthesis.</td></tr><tr><td>Type</td><td colspan="2">text</td></tr><tr><td>Answer</td><td colspan="2">Plants convert sunlight, water, and CO2 into glucose and oxygen.</td></tr><tr><td>Solution</td><td colspan="2">6CO2 + 6H2O -> C6H12O6 + 6O2</td></tr><tr><td>Marks</td><td>5</td><td>0</td></tr></table>
<h1 style="margin-top:60px;border-top:3px solid #333;padding-top:20px">Comprehension (Context + Child Questions)</h1>
<p><b>How it works:</b> A table with <code>Type = comprehension</code> is the parent (passage/context). All following tables become its <b>child questions</b> UNTIL:</p>
<ul>
<li>Another <code>comprehension</code> table appears (starts a new comprehension group)</li>
<li>A table with <code>Type = end_comprehension</code> appears (ends the group, next questions are standalone)</li>
</ul>
<p style="color:#c62828"><b>Important:</b> To add standalone questions AFTER a comprehension block, you MUST insert a separator table with <code>Type = end_comprehension</code>. Without it, all remaining questions will be treated as children of the comprehension.</p>

<h2>10. Comprehension - Parent (Passage)</h2>
<table><tr><td>Question</td><td colspan="2">Read the following passage carefully:<br><br>Water is essential for all life on Earth. About 71% of the Earth's surface is covered by water. The oceans hold about 96.5% of all Earth's water. Only 2.5% of Earth's water is freshwater, and most of that is locked in ice caps and glaciers. The water cycle involves evaporation, condensation, precipitation, and collection.</td></tr><tr><td>Type</td><td colspan="2">comprehension</td></tr><tr><td>Solution</td><td colspan="2"></td></tr><tr><td>Marks</td><td>0</td><td>0</td></tr></table>
<h3>Child 1: SCQ</h3>
<table><tr><td>Question</td><td colspan="2">What percentage of Earth's surface is covered by water?</td></tr><tr><td>Type</td><td colspan="2">single_choice</td></tr><tr><td>Option</td><td>50%</td><td>incorrect</td></tr><tr><td>Option</td><td>71%</td><td>correct</td></tr><tr><td>Option</td><td>96.5%</td><td>incorrect</td></tr><tr><td>Option</td><td>2.5%</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">The passage states about 71%.</td></tr><tr><td>Marks</td><td>2</td><td>1</td></tr></table>
<h3>Child 2: MCQ</h3>
<table><tr><td>Question</td><td colspan="2">Which of the following are stages in the water cycle?</td></tr><tr><td>Type</td><td colspan="2">multiple_choice</td></tr><tr><td>Option</td><td>Evaporation</td><td>correct</td></tr><tr><td>Option</td><td>Photosynthesis</td><td>incorrect</td></tr><tr><td>Option</td><td>Condensation</td><td>correct</td></tr><tr><td>Option</td><td>Precipitation</td><td>correct</td></tr><tr><td>Solution</td><td colspan="2">Evaporation, condensation, and precipitation are in the passage.</td></tr><tr><td>Marks</td><td>4</td><td>1</td></tr></table>
<h3>Child 3: Integer</h3>
<table><tr><td>Question</td><td colspan="2">What percentage of Earth's water is freshwater? (Round to nearest integer)</td></tr><tr><td>Type</td><td colspan="2">integer</td></tr><tr><td>Answer</td><td colspan="2">2</td></tr><tr><td>Solution</td><td colspan="2">2.5% rounds to 2 or 3.</td></tr><tr><td>Marks</td><td>2</td><td>0</td></tr></table>

<h2 style="color:#c62828;margin-top:40px">End Comprehension Separator</h2>
<p>This table tells the parser that the comprehension block has ended. Questions after this are standalone.</p>
<table><tr><td>Type</td><td colspan="2">end_comprehension</td></tr></table>

<h2>11. Standalone SCQ (after comprehension)</h2>
<table><tr><td>Question</td><td colspan="2">What is the boiling point of water at sea level?</td></tr><tr><td>Type</td><td colspan="2">single_choice</td></tr><tr><td>Option</td><td>90&deg;C</td><td>incorrect</td></tr><tr><td>Option</td><td>100&deg;C</td><td>correct</td></tr><tr><td>Option</td><td>110&deg;C</td><td>incorrect</td></tr><tr><td>Option</td><td>120&deg;C</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">Water boils at 100&deg;C at standard atmospheric pressure.</td></tr><tr><td>Marks</td><td>2</td><td>0</td></tr></table>

<h2>12. Another Standalone MCQ</h2>
<table><tr><td>Question</td><td colspan="2">Select the noble gases:</td></tr><tr><td>Type</td><td colspan="2">multiple_choice</td></tr><tr><td>Option</td><td>Helium</td><td>correct</td></tr><tr><td>Option</td><td>Nitrogen</td><td>incorrect</td></tr><tr><td>Option</td><td>Neon</td><td>correct</td></tr><tr><td>Option</td><td>Oxygen</td><td>incorrect</td></tr><tr><td>Solution</td><td colspan="2">Helium and Neon are noble gases.</td></tr><tr><td>Marks</td><td>4</td><td>1</td></tr></table>
</body></html>`;

    const blob = new Blob([html], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question-ingestion-sample.doc';
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderUpload = () => (
    <div className="import-docx-upload">
      <h5>Import Questions from DOCX</h5>
      <p className="text-muted" style={{ fontSize: '0.9rem' }}>
        Upload a Word document (.docx) with questions formatted as tables.
        Each table should have rows for: Question, Type, Option(s), Solution, and Marks.
      </p>
      <div
        style={{
          background: '#fff8e1',
          border: '1px solid #ffd54f',
          padding: '8px 12px',
          borderRadius: 4,
          fontSize: '0.88rem',
          marginBottom: 12,
        }}
      >
        Got a document in a different format (numbered questions, inline <code>(A)/(B*)/(C)/(D)</code>{' '}
        options, <code>Ans.</code> markers)?{' '}
        <Link to="/question-bank/convert">Convert it to this format first &rarr;</Link>
      </div>

      <Button
        size="sm"
        onClick={handleDownloadSample}
        style={{ marginBottom: 16, backgroundColor: 'var(--status-success)', borderColor: 'var(--status-success)', color: 'var(--text-on-brand)' }}
      >
        Download Sample Template
      </Button>

      <div className="import-docx-format-guide">
        <h6>Supported Question Types</h6>
        <table className="import-docx-guide-table">
          <thead>
            <tr><th>Type (in DOCX)</th><th>Row Format</th><th>Notes</th></tr>
          </thead>
          <tbody>
            <tr><td><code>single_choice</code></td><td>Option rows with correct/incorrect</td><td>Exactly 1 correct answer</td></tr>
            <tr><td><code>multiple_choice</code></td><td>Option rows with correct/incorrect</td><td>1 or more correct answers</td></tr>
            <tr><td><code>integer</code></td><td>Answer row with number</td><td>Whole number answer</td></tr>
            <tr><td><code>true_false</code></td><td>2 Option rows (True/False)</td><td>Exactly 1 must be correct</td></tr>
            <tr><td><code>match_the_following</code></td><td>Left rows: col2=left, col3=right</td><td>Min 2 pairs required</td></tr>
            <tr><td><code>integer_range</code></td><td>Answer row: col2=min, col3=max</td><td>Any value in range is correct</td></tr>
            <tr><td><code>fill_in_the_blanks</code></td><td>Blank rows (one per blank)</td><td>Use ____ in question text</td></tr>
            <tr><td><code>fraction</code></td><td>Answer row with decimal number</td><td>Accepts decimal values</td></tr>
            <tr><td><code>text</code></td><td>Answer row with model answer</td><td>Subjective / essay type</td></tr>
            <tr><td><code>comprehension</code></td><td>Passage table followed by child question tables</td><td>Children can be any type</td></tr>
            <tr><td><code>end_comprehension</code></td><td>Empty separator table (just Type row)</td><td>Ends comprehension block; questions after are standalone</td></tr>
          </tbody>
        </table>

        <h6 style={{ marginTop: 12 }}>Table Structure</h6>
        <ul style={{ fontSize: '0.85rem', color: 'var(--text-body)', paddingLeft: 20 }}>
          <li><strong>Row 1:</strong> Question &mdash; question text (supports bold, italic, images, math formulas)</li>
          <li><strong>Row 2:</strong> Type &mdash; one of the type values above</li>
          <li><strong>Row 3+:</strong> Option / Left / Blank / Answer rows (depends on type)</li>
          <li><strong>Solution:</strong> Explanation (optional, supports rich text)</li>
          <li><strong>Marks:</strong> Col2 = positive marks, Col3 = negative marks</li>
        </ul>
      </div>

      <div className="import-docx-file-picker">
        <Form.Group>
          <Form.Label>Select DOCX File</Form.Label>
          <Form.Control
            ref={fileInputRef}
            type="file"
            accept=".docx"
            onChange={handleFileChange}
          />
        </Form.Group>
        {selectedFile && (
          <div style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text-body)' }}>
            Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </div>
        )}
      </div>

      <Button
        variant="primary"
        onClick={handleParse}
        disabled={!selectedFile || loading}
        style={{ marginTop: 16 }}
      >
        {loading ? <><Spinner role="status" animation="border" size="sm" /> Parsing...</> : 'Parse Questions'}
      </Button>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /*  Render: Preview Stage                                             */
  /* ---------------------------------------------------------------- */

  const renderTagSelector = (questionKey: string) => {
    if (!lookups) return null;
    const selectedTags = tagAssignments[questionKey] || [];

    // Group tags by tag type
    const tagTypeGroups: Record<number, { typeName: string; tags: { id: number; name: string }[] }> = {};
    for (const tag of lookups.tags) {
      if (!tagTypeGroups[tag.typeId]) {
        const tt = lookups.tagTypes.find((t) => t.id === tag.typeId);
        tagTypeGroups[tag.typeId] = { typeName: tt?.name || `Type ${tag.typeId}`, tags: [] };
      }
      tagTypeGroups[tag.typeId].tags.push(tag);
    }

    return (
      <div className="import-docx-tags">
        {Object.entries(tagTypeGroups).map(([typeId, group]) => (
          <div key={typeId} className="import-docx-tag-group">
            <label className="import-docx-tag-label">{group.typeName}</label>
            <Form.Select
              size="sm"
              value=""
              onChange={(e) => {
                const val = Number(e.target.value);
                if (val && !selectedTags.includes(val)) {
                  handleTagChange(questionKey, val, true);
                }
              }}
              style={{ width: 180, flex: '0 0 auto' }}
            >
              <option value="">Select...</option>
              {group.tags.map((tag) => (
                <option key={tag.id} value={tag.id} disabled={selectedTags.includes(tag.id)}>
                  {tag.name}
                </option>
              ))}
            </Form.Select>
            <div className="import-docx-selected-tags">
              {selectedTags
                .filter((id) => group.tags.some((t) => t.id === id))
                .map((tagId) => {
                  const tag = group.tags.find((t) => t.id === tagId);
                  return tag ? (
                    <span key={tagId} className="import-docx-tag-chip">
                      {tag.name}
                      <span
                        className="import-docx-tag-remove"
                        onClick={() => handleTagChange(questionKey, tagId, false)}
                      >
                        x
                      </span>
                    </span>
                  ) : null;
                })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleDropQuestion = (index: number, childIndex?: number) => {
    setQuestions(prev => {
      const updated = [...prev];
      if (childIndex !== undefined) {
        // Remove a child from a comprehension group
        const parent = { ...updated[index], children: [...(updated[index].children || [])] };
        parent.children.splice(childIndex, 1);
        if (parent.children.length === 0) {
          // No children left — remove the whole group
          updated.splice(index, 1);
        } else {
          updated[index] = parent;
        }
      } else {
        updated.splice(index, 1);
      }
      return updated;
    });
  };

  const renderQuestionCard = (q: ParsedQuestion, index: number, isChild: boolean, parentIndex: number | undefined, runningNumber: number) => {
    const questionKey = isChild ? `${parentIndex}-${index}` : String(index);
    const hasErrors = q.errors.length > 0;
    const typeLabel = CATEGORY_LABELS[q.categoryId] || q.type;
    const qNumber = `${runningNumber}`;

    return (
      <Card
        key={questionKey}
        className={`import-docx-question-card ${hasErrors ? 'has-errors' : ''} ${isChild ? 'is-child' : ''}`}
      >
        <Card.Header className="import-docx-card-header">
          <div className="import-docx-q-info">
            <span className="import-docx-q-number">Q{qNumber}</span>
            <span
              className="import-docx-type-label"
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 4,
                fontSize: '0.75rem',
                fontWeight: 600,
                backgroundColor: (CATEGORY_COLORS[q.categoryId] || { bg: '#e8f5e9' }).bg,
                color: (CATEGORY_COLORS[q.categoryId] || { fg: '#2e7d32' }).fg,
              }}
            >
              {typeLabel}
            </span>
            {q.positiveMarks > 0 && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>
                +{q.positiveMarks} / -{q.negativeMarks}
              </span>
            )}
            <button
              className="import-docx-drop-btn"
              title="Remove this question"
              onClick={() => isChild ? handleDropQuestion(parentIndex!, index) : handleDropQuestion(index)}
            >
              &times;
            </button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Errors */}
          {hasErrors && (
            <div className="import-docx-errors">
              {q.errors.map((err, ei) => (
                <div key={ei} className="import-docx-error-item">{err}</div>
              ))}
            </div>
          )}

          {/* Question text */}
          <div
            className="import-docx-question-text"
            dangerouslySetInnerHTML={{ __html: q.questionStatement }}
          />

          {/* Options */}
          {q.choices.length > 0 && (
            <div className="import-docx-options">
              {q.choices.map((choice, ci) => (
                <div
                  key={ci}
                  className={`import-docx-option ${choice.isCorrect ? 'correct' : ''}`}
                >
                  <span className="import-docx-option-letter">{choice.choiceString}.</span>
                  <span dangerouslySetInnerHTML={{ __html: choice.text }} />
                  {choice.isCorrect && <span className="import-docx-correct-mark">&#10003;</span>}
                </div>
              ))}
            </div>
          )}

          {/* Match the Following pairs */}
          {q.matchPairs && q.matchPairs.length > 0 && (
            <div className="import-docx-mtf">
              <table className="import-docx-mtf-table">
                <thead>
                  <tr><th>#</th><th>Left</th><th>Right</th></tr>
                </thead>
                <tbody>
                  {q.matchPairs.map((pair: any, pi: number) => (
                    <tr key={pi}>
                      <td>{pi + 1}</td>
                      <td dangerouslySetInnerHTML={{ __html: pair.left }} />
                      <td dangerouslySetInnerHTML={{ __html: pair.right }} />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Fill in the Blanks answers */}
          {q.blanks && q.blanks.length > 0 && (
            <div className="import-docx-blanks">
              {q.blanks.map((blank: any, bi: number) => (
                <div key={bi} className="import-docx-blank-item">
                  <strong>Blank {bi + 1}:</strong> {blank.answer}
                </div>
              ))}
            </div>
          )}

          {/* Integer / Fraction / Text answer */}
          {q.textAnswer && q.categoryId !== 5 && (
            <div className="import-docx-answer">
              <strong>Answer:</strong> {q.textAnswer}
            </div>
          )}

          {/* Integer Range answer */}
          {q.categoryId === 5 && q.answerRangeStart !== undefined && (
            <div className="import-docx-answer">
              <strong>Range:</strong> {q.answerRangeStart} to {q.answerRangeEnd}
            </div>
          )}

          {/* Solution (collapsible) */}
          {q.solution && (
            <Accordion className="import-docx-solution-accordion">
              <Accordion.Item eventKey="0">
                <Accordion.Header>Solution</Accordion.Header>
                <Accordion.Body>
                  <div dangerouslySetInnerHTML={{ __html: q.solution }} />
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          )}

          {/* Tags */}
          {!hasErrors && q.categoryId !== -1 && renderTagSelector(questionKey)}
        </Card.Body>
      </Card>
    );
  };

  const renderPreview = () => {
    const validCount = countValidQuestions();
    const totalCount = questions.reduce(
      (acc, q) => acc + (q.categoryId === -1 && q.children ? q.children.length : 1),
      0,
    );
    const invalidQuestions = collectInvalidQuestions();
    const hasInvalid = invalidQuestions.length > 0;

    return (
      <div className="import-docx-preview">
        <div className="import-docx-preview-header">
          <h5>
            Parsed Questions ({validCount} valid / {totalCount} total)
          </h5>
          <div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleParse}
              disabled={loading}
              style={{ marginRight: 8 }}
              title="Re-parse the same file (useful after editing the DOCX externally)"
            >
              {loading ? <><Spinner role="status" size="sm" animation="border" /> Reloading...</> : 'Reload'}
            </Button>
            <Button variant="secondary" size="sm" onClick={handleReset} style={{ marginRight: 8 }}>
              Upload Different File
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowConfirmModal(true)}
              disabled={validCount === 0 && !hasInvalid}
            >
              Import {validCount} Questions
            </Button>
          </div>
        </div>

        {/* Bulk tag assignment */}
        {lookups && lookups.tagTypes && lookups.tagTypes.length > 0 && (
          <div className="import-docx-bulk-tags" style={{ padding: 16 }}>
            {/* Header */}
            <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border-default)' }}>
              Apply tags to all questions
            </div>

            {/* Tag selectors */}
            <QuestionTagSelector
              tagTypes={lookups.tagTypes}
              tagSelections={bulkTagSelections}
              onTagSelectionsChange={setBulkTagSelections}
              addedTags={bulkAddedTags}
              onAddedTagsChange={setBulkAddedTags}
            />

            {/* Apply button at bottom */}
            <div style={{ borderTop: '1px solid var(--border-default)', paddingTop: 10, marginTop: 10, textAlign: 'right' }}>
              <Button
                variant="primary"
                size="sm"
                onClick={handleBulkTagApply}
                disabled={bulkAddedTags.length === 0}
                style={{ minWidth: 80 }}
              >
                Apply
              </Button>
            </div>
          </div>
        )}

        {/* Question cards */}
        <div className="import-docx-questions-list">
          {(() => {
            let running = 0;
            let passageNum = 0;
            return questions.map((q, i) => {
              if (q.categoryId === -1 && q.children) {
                passageNum += 1;
                const startNumber = running + 1;
                const endNumber = running + q.children.length;
                const childCards = q.children.map((child, j) => {
                  running += 1;
                  return renderQuestionCard(child, j, true, i, running);
                });
                return (
                  <div key={i} className="import-docx-comprehension-group">
                    <Card className="import-docx-passage-card">
                      <Card.Header>
                        <span className="import-docx-q-number">Passage {passageNum}</span>
                        <span
                          style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            borderRadius: 4,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                          }}
                        >
                          Comprehension
                        </span>
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: 8 }}>
                          {q.children.length === 1 ? `Q${startNumber}` : `Q${startNumber}\u2013Q${endNumber}`}
                        </span>
                      </Card.Header>
                      <Card.Body>
                        <div
                          className="import-docx-question-text"
                          dangerouslySetInnerHTML={{ __html: q.questionStatement }}
                        />
                      </Card.Body>
                    </Card>
                    <div className="import-docx-children">{childCards}</div>
                  </div>
                );
              }
              running += 1;
              return renderQuestionCard(q, i, false, undefined, running);
            });
          })()}
        </div>

        {/* Confirmation Modal */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{hasInvalid ? 'Cannot Import — Validation Errors' : 'Confirm Import'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {hasInvalid ? (
              <>
                <div style={{ marginBottom: 12 }}>
                  {invalidQuestions.length} of {totalCount} question{totalCount > 1 ? 's' : ''} {invalidQuestions.length > 1 ? 'have' : 'has'} validation errors and must be fixed before importing.
                  Correct the answer in the DOCX and click <strong>Reload</strong>, or remove the flagged question{invalidQuestions.length > 1 ? 's' : ''} with the &times; button.
                </div>
                <div style={{ fontWeight: 600, color: '#c62828', marginBottom: 8 }}>
                  {invalidQuestions.length} question{invalidQuestions.length > 1 ? 's' : ''} with errors:
                </div>
                <div
                  style={{
                    maxHeight: 260,
                    overflowY: 'auto',
                    border: '1px solid #f1c0c0',
                    borderRadius: 6,
                    padding: '8px 12px',
                    background: '#fff5f5',
                  }}
                >
                  {invalidQuestions.map((item, idx) => (
                    <div key={idx} style={{ marginBottom: idx === invalidQuestions.length - 1 ? 0 : 10 }}>
                      <span style={{ fontWeight: 600 }}>{item.label}</span>
                      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                        {item.errors.map((err, ei) => (
                          <li key={ei} style={{ color: '#c62828', fontSize: '0.85rem' }}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>Are you sure you want to import {validCount} questions into the question bank?</>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
              {hasInvalid ? 'Close' : 'Cancel'}
            </Button>
            {!hasInvalid && (
              <Button variant="primary" onClick={handleImport}>
                Import
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render: Done Stage                                                */
  /* ---------------------------------------------------------------- */

  const handleQuickPaper = () => {
    const ids = importedIds.join(',');
    window.location.href = `/quick-quiz-maker?questionIds=${ids}&returnTo=question-bank-import`;
  };

  const [idsCopied, setIdsCopied] = useState(false);

  const handleCopyIds = () => {
    navigator.clipboard.writeText(importedIds.join(', ')).then(() => {
      setIdsCopied(true);
      setTimeout(() => setIdsCopied(false), 2000);
    });
  };

  const renderDone = () => (
    <div className="import-docx-done">
      <div className="import-docx-success-icon">&#10003;</div>
      <h5>Successfully imported {importedIds.length} questions</h5>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
        Question IDs: {importedIds.join(', ')}
        <span
          title={idsCopied ? 'Copied!' : 'Copy IDs'}
          onClick={handleCopyIds}
          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: idsCopied ? '#28a745' : '#888', flexShrink: 0 }}
        >
          {idsCopied ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          )}
        </span>
      </p>
      <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button className="import-docx-quick-paper-btn" onClick={handleQuickPaper}>
          Help me quickly make a paper
        </Button>
        <Button variant="primary" onClick={handleReset}>
          Import More Questions
        </Button>
        <Button variant="secondary" onClick={() => window.location.href = '/question-bank'}>
          Go to Question Bank
        </Button>
      </div>
    </div>
  );

  /* ---------------------------------------------------------------- */
  /*  Main Render                                                       */
  /* ---------------------------------------------------------------- */

  return (
    <div className="import-docx-page" ref={rootRef}>
      {loading && stage !== 'upload' && (
        <div className="import-docx-loading-overlay">
          <Spinner role="status" animation="border" variant="primary" />
          <span style={{ marginLeft: 12 }}>Processing...</span>
        </div>
      )}

      {error && (
        <div className="import-docx-error-banner">
          {error}
          <span className="import-docx-error-close" onClick={() => setError('')}>x</span>
        </div>
      )}

      {stage === 'upload' && renderUpload()}
      {stage === 'preview' && renderPreview()}
      {stage === 'done' && renderDone()}
    </div>
  );
};

export default ImportDocx;
